import {
  Clear as ClearIcon,
  FilterAlt as FilterIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import React from "react";
import { WorkTypeEnum } from "../../store/types/work.types";
import { WorksFilters as Filters } from "../../types/WorksList.types";
import { getWorkTypeColor, getWorkTypeName } from "../../utils/workUtils";

interface WorksFiltersProps {
  filters: Filters;
  onFiltersChange: (filters: Filters) => void;
  showFilters: boolean;
  onToggleFilters: () => void;
}

const workTypeOptions = Object.values(WorkTypeEnum)
  .filter((v): v is WorkTypeEnum => typeof v === "number" && v !== 0)
  .sort((a, b) => getWorkTypeName(a).localeCompare(getWorkTypeName(b)));

const WorksFilters: React.FC<WorksFiltersProps> = ({
  filters,
  onFiltersChange,
  showFilters,
  onToggleFilters,
}) => {
  const hasActiveFilters =
    filters.dateFilter.from ||
    filters.dateFilter.to ||
    filters.search ||
    filters.workTypes.length > 0;

  const clearFilters = () => {
    onFiltersChange({
      ...filters,
      dateFilter: { from: null, to: null },
      search: "",
      workTypes: [],
    });
  };

  return (
    <Box>
      <Box sx={{ display: "flex", mb: 2 }}>
        <TextField
          fullWidth
          placeholder="Sök ärenden..."
          value={filters.search}
          onChange={(e) =>
            onFiltersChange({ ...filters, search: e.target.value })
          }
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: filters.search && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => onFiltersChange({ ...filters, search: "" })}
                >
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          size="small"
          sx={{ mr: 1 }}
        />

        <Tooltip title="Filtrera">
          <IconButton
            onClick={onToggleFilters}
            color={hasActiveFilters ? "primary" : "default"}
          >
            <FilterIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Collapse in={showFilters}>
        <Box sx={{ mb: 2, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
          <Stack spacing={2}>
            <Stack direction="row" spacing={2} alignItems="center">
              <DatePicker
                label="Från datum"
                value={filters.dateFilter.from}
                onChange={(date) =>
                  onFiltersChange({
                    ...filters,
                    dateFilter: { ...filters.dateFilter, from: date },
                  })
                }
                slotProps={{
                  textField: {
                    size: "small",
                    sx: { minWidth: 150 },
                  },
                }}
              />
              <DatePicker
                label="Till datum"
                value={filters.dateFilter.to}
                onChange={(date) =>
                  onFiltersChange({
                    ...filters,
                    dateFilter: { ...filters.dateFilter, to: date },
                  })
                }
                slotProps={{
                  textField: {
                    size: "small",
                    sx: { minWidth: 150 },
                  },
                }}
              />
            </Stack>

            <Autocomplete
              multiple
              options={workTypeOptions}
              value={filters.workTypes}
              onChange={(_, value) =>
                onFiltersChange({ ...filters, workTypes: value })
              }
              getOptionLabel={(option) => getWorkTypeName(option)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Ärendetyp"
                  size="small"
                  placeholder="Välj typer..."
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option}
                    label={getWorkTypeName(option)}
                    size="small"
                    sx={{
                      bgcolor: `${getWorkTypeColor(option)}20`,
                      color: getWorkTypeColor(option),
                      borderColor: getWorkTypeColor(option),
                    }}
                    variant="outlined"
                  />
                ))
              }
            />

            {hasActiveFilters && (
              <Box>
                <Button
                  startIcon={<ClearIcon />}
                  onClick={clearFilters}
                  size="small"
                  variant="outlined"
                >
                  Rensa filter
                </Button>
              </Box>
            )}
          </Stack>
        </Box>
      </Collapse>
    </Box>
  );
};

export default WorksFilters;
